// @ts-strict-ignore
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import moment from 'moment-timezone';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import memoize from 'lodash/memoize';
import isNumber from 'lodash/isNumber';

import {capitalize, truncateMiddle} from 'common/util/string';
import {
  BirthIntervalUnit,
  LiftCell,
  LiftRow,
  MetricKeyInfo,
  NON_INTERVALIZED_VALUE,
  RetentionBookmarkParams,
  RetentionDataTableCell,
  RetentionDataTableRow,
  TableCell,
} from 'common/report/retention/util/types';
import {FormattedRow, RawCell} from 'common/report/retention/util/types';
import {
  Addiction,
  isUnsegmentedData,
  KeyedByDate,
  KeyedBySegment,
  Retention,
  RetentionData,
  SegmentKeyToPath,
} from 'common/report/retention/models/types';
import {AGGREGATED_ROW_NAME} from 'common/report/retention/util/constants';
import {colors, getChartSegmentColorCombo, getRetentionColorCombo} from 'common/util/colors';
import {formatNumber, formatPercent, getRetentionBucketName} from 'common/report/retention/util';
import {getSegmentPropertyType, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {
  CellRendererFunc,
  ColumnGroup,
  CommonDataTableFields,
  TableColumn,
  TableColumnType,
  TableDataType,
} from 'common/components/data-table/types';
import {PropertyType} from 'common/report/constants';
import {LiftType} from 'common/report/util/lift/types';
import {DEFAULT_LIFT_COLUMN_WIDTH} from 'common/components/data-table/constants';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {formatLiftPercent} from 'common/report/util/lift';
import {
  BehaviorMetricDisplay,
  BehaviorShowClause,
  GroupClause,
  isInsightsBookmarkParams,
  MetricBehavior,
  MetricMeasurement,
} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams, RetentionType} from 'common/types/reports/bookmark';
import {Report} from 'common/report/insights/models/report';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getCalendarDate} from 'common/util/date';
import {type RetentionBookmarkController} from 'common/report/retention/bookmark-controller';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {renamePropertyValue} from 'common/query-builder/cohorts-query-builder/util';
import {LocalizationStore} from 'common/i18n/store';

import {getDataGroupName, getGroupByName, getSegmentName, isSegmented} from './format';

const MAX_TITLE_CELL_LENGTH = 60;

type BaseRow = {
  date: Date;
  cohort_size: number;
  data: number[];
  rates?: number[];
  complete_date: Array<Date | null>;
};

type SegmentRow = BaseRow & {
  segment: string;
};

type Row = BaseRow | SegmentRow;
/** Aggregate row: data + optional segment, but no date */
type AverageRow = Omit<BaseRow, `date`> & {
  segment?: string;
  /** not calculated the same way as normal rows */
  percent_data?: number[];
};

type SuperRow = Row | AverageRow;

/** A collection of dated rows and their aggregate, belonging to a segment. */
export type RowWithAggregate = {
  /** An aggregate of all of the rows in datedRows. */
  aggregate: AverageRow;
  /** Collection of rows for this segment, ordered by birth interval. */
  datedRows: Row[];
  /** If empty, represents unsegmented data. */
  segment?: string;
  /** A matching show clause index of the row */
  showClauseIndex?: number;
};

export function isAverageRow(row: SuperRow): row is AverageRow {
  return !(`date` in row);
}

const WITH_YEAR = `MMM D, YYYY`;
const WITHOUT_YEAR = `MMM D`;
export const formatDate = memoize(function (date: Date, timezone: string, includeYear = true): string {
  return moment.tz(date, timezone).format(includeYear ? WITH_YEAR : WITHOUT_YEAR);
});

function getRowHead(row: SuperRow, retentionType: RetentionType, timezone: string, isGroupedRow) {
  if (`segment` in row && !isGroupedRow) {
    // prefer segment even for average rows
    return stringifySegment(row.segment);
  } else if (`date` in row) {
    return formatDate(row.date, timezone);
  } else {
    // only use this when no segment is available
    return AGGREGATED_ROW_NAME(retentionType);
  }
}

export function getCellsForRow(retentionType: RetentionType, timezone: string, groupClauses: GroupClause[]) {
  return function _getCellsForRow(row: SuperRow, idx: number, isGroupedRow = false): FormattedRow<RawCell> {
    const data = row.data.map(getCellForIndex(row, timezone, groupClauses));
    const rowHead = getRowHead(row, retentionType, timezone, isGroupedRow);
    const truncatedHead =
      rowHead.length > MAX_TITLE_CELL_LENGTH ? truncateMiddle(rowHead, MAX_TITLE_CELL_LENGTH) : rowHead;

    return {
      cohort_size: row.cohort_size,
      data,
      display_cohort_size: formatNumber(row.cohort_size),
      head: rowHead,
      display_head: truncatedHead,
      raw_head: rowHead,
      selection_color: getChartSegmentColorCombo(idx).primary,
    };
  };
}

function stringifySegment(seg: string | string[]): string {
  if (Array.isArray(seg)) {
    return seg.join(` / `);
  } else {
    return seg;
  }
}

function stringifySegmentForDisplay(path: string | string[], groupClauses: GroupClause[]) {
  return stringifySegment(path)
    .split(` / `)
    .map((segmentPart, i) => {
      const groupClause = groupClauses[i];
      return renamePropertyValue(segmentPart, groupClause?.value, groupClause);
    })
    .join(` / `);
}

export function getCellForIndex(row: SuperRow, timezone: string, groupClauses: GroupClause[]) {
  return function _getCellForIndex(value: number, valIdx: number): RawCell {
    const completeDate = row.complete_date[valIdx];
    const incomplete = !!completeDate;
    let enteredValue = null;

    if (`segment` in row) {
      enteredValue = stringifySegmentForDisplay(row.segment, groupClauses);
    } else if (isAverageRow(row)) {
      enteredValue = `$average`; // this doesn't get consumed anywhere
    } else {
      enteredValue = formatDate(row.date, timezone);
    }

    let cohortSize: number, percent: number;
    // If `rates` is defined in row, it implies that row is a retention data.
    // If `rates` is undefined in row, it implies that row is a frequency/addiction data.
    // cohortSize is defined differently depending on the type of data
    if (`rates` in row) {
      percent = row.rates[valIdx];
      cohortSize = percent > 0 ? Math.round((value / percent) * 10000) / 10000 : 0;
    } else {
      cohortSize = row.cohort_size;
      percent = `percent_data` in row ? row.percent_data[valIdx] : cohortSize > 0 ? value / cohortSize : 0;
    }

    return {
      date: `date` in row ? row.date : null,
      cohort_size: cohortSize,
      complete_date: incomplete && formatDate(completeDate, timezone, false),
      incomplete,
      percent,
      value,
      formatted_percent: formatPercent(percent) + `%`,
      formatted_percent_drop_off_value: formatPercent(1 - percent) + `%`,
      percent_drop_off_value: 1 - percent,
      formatted_value: formatNumber(value),
      formatted_absolute_drop_off_value: formatNumber(cohortSize - value),
      absolute_drop_off_value: cohortSize - value,
      selectable: true,
      entered_value: enteredValue,
    };
  };
}

const DEFAULT_UNSEG_BUCKET = `Overall`;

export function getUiRowsDataFromParsed({
  data,
  incompleteBuckets,
  bookmarkParams,
  timezone,
  compareToPastData,
  pastIncompleteBuckets,
  liftData,
  metricKeyLookup = null,
}: {
  data: RetentionData;
  incompleteBuckets: {
    [date: string]: Array<null | string>;
  };
  bookmarkParams: RetentionBookmarkParams | InsightsBookmarkParams;
  timezone: any;
  compareToPastData: RetentionData;
  pastIncompleteBuckets: {[date: string]: string[]};
  liftData: RetentionData;
  metricKeyLookup?: Record<string, MetricKeyInfo> | null;
}) {
  let formattedUiRows = Array<FormattedRow<RawCell>>();
  let formattedComparisonRowsData = new Map<string, FormattedRow>();
  let formattedLiftRowsData = new Map<string, LiftRow>();
  let liftRowsDataMap = new Map<string, RowWithAggregate>();

  let segments: Segment[],
    retentionType: RetentionType,
    chartType: ChartType,
    dateUnit: BirthIntervalUnit,
    retentionBucket: number;
  if (isInsightsBookmarkParams(bookmarkParams)) {
    segments = getSelectedSegments(Report.fromBookmarkData(bookmarkParams).sections.group.clauses);
    chartType = bookmarkParams.displayOptions.chartType;
    if (chartType === ChartType.RetentionCurve) {
      chartType = ChartType.Table;
    }
    // TODO: Should throw error if sections.show has more than 1
    const behavior: MetricBehavior = (bookmarkParams.sections.show[0] as BehaviorShowClause).behavior;
    const measurement: MetricMeasurement = (bookmarkParams.sections.show[0] as BehaviorShowClause).measurement;
    retentionType = behavior.retentionType;
    dateUnit = bookmarkParams.sections.time[0].unit;
    retentionBucket = measurement.retentionBucketIndex;
  } else {
    segments = bookmarkParams.group_by;
    chartType = bookmarkParams.chart_type as unknown as ChartType;
    retentionType = bookmarkParams.retention_type;
    dateUnit = bookmarkParams.date_unit;
    retentionBucket = bookmarkParams.retention_bucket;
  }

  const dataRows = getFormattedRowData({data, incompleteBuckets, segmentGroups: segments, timezone, metricKeyLookup});
  const compareToPastDataRows = compareToPastData
    ? getFormattedRowData({
        data: compareToPastData,
        incompleteBuckets: pastIncompleteBuckets,
        segmentGroups: segments,
        timezone,
        metricKeyLookup,
      })
    : [];
  const liftDataRows = liftData
    ? getFormattedRowData({data: liftData, incompleteBuckets, segmentGroups: segments, timezone, metricKeyLookup})
    : [];

  function mapFormattedRowsData(formattedUIRows): Map<string, FormattedRow> {
    return new Map(
      formattedUIRows.map((segmentRow) => [
        segmentRow.raw_head,
        {...segmentRow, selection_color: getCompareRowColor(segmentRow)},
      ]),
    );
  }

  if (hasRetentionData(dataRows) || hasRetentionData(compareToPastDataRows)) {
    const groupClauses = `sections` in bookmarkParams ? bookmarkParams.sections.group : [];
    formattedUiRows = mapToUiRows({
      chartType,
      dateUnit,
      groupClauses,
      retentionBucket,
      retentionType,
      segments,
      rowData: dataRows,
      timezone,
    });

    const formattedComparedUiRows =
      compareToPastDataRows.length > 0
        ? mapToUiRows({
            chartType,
            dateUnit,
            groupClauses,
            retentionBucket,
            retentionType,
            rowData: compareToPastDataRows,
            segments,
            timezone,
          })
        : [];

    formattedComparisonRowsData = mapFormattedRowsData(formattedComparedUiRows);
  }
  if (hasRetentionData(liftDataRows)) {
    const formattedLiftUiRows = mapLiftToUiRows({
      liftRowData: liftDataRows,
      timezone,
      retentionType,
      chartType,
      segments,
      dateUnit,
      retentionBucket,
    });

    formattedLiftRowsData = mapFormattedRowsData(formattedLiftUiRows);

    // build another construct of lift data, that only has what we need
    liftRowsDataMap = new Map(liftDataRows.map((liftRow) => [liftRow.aggregate.segment, liftRow]));
  }

  function getCompareRowColor(compareRow) {
    return formattedUiRows.find((row) => row.raw_head === compareRow.raw_head).selection_color;
  }
  return {formattedUiRows, formattedComparisonRowsData, formattedLiftRowsData, liftRowsDataMap};
}

export function mapLiftToUiRows({
  liftRowData,
  timezone,
  retentionType,
  chartType,
  segments,
  dateUnit,
  retentionBucket,
}: {
  liftRowData: RowWithAggregate[];
  timezone: string;
  retentionType: RetentionType;
  chartType: ChartType;
  segments: Segment[];
  dateUnit: BirthIntervalUnit;
  retentionBucket: number;
}): LiftRow[] {
  let rows = [];
  if (chartType === ChartType.Table || chartType === ChartType.Line) {
    rows = liftRowData.map((group) => {
      const averageRow = group.aggregate;
      const liftRow = getLiftRow(retentionType, timezone, averageRow);

      if (isSegmented(segments)) {
        liftRow.raw_head = liftRow.display_head = liftRow.head = stringifySegment(group.aggregate.segment);
      }
      if (dateUnit !== NON_INTERVALIZED_VALUE) {
        liftRow.groupedRows = group.datedRows.map((groupedRow) =>
          getLiftRow(retentionType, timezone, groupedRow, true),
        );
      }

      return liftRow;
    });
  } else if (chartType === ChartType.RetentionTrend || chartType === ChartType.RetentionTrendMetric) {
    const buildDataFromDatedRows = function (datedRows) {
      return datedRows.reduce((accumData, dr) => {
        if (dr.data.length > retentionBucket) {
          accumData.push(getLiftCellForIndex(dr, timezone)(dr.data[retentionBucket], retentionBucket));
        }
        return accumData;
      }, []);
    };
    if (isSegmented(segments)) {
      // segmented
      rows = liftRowData.map((segmentRow) => {
        const data = buildDataFromDatedRows(segmentRow.datedRows);
        const segment = stringifySegment(segmentRow.aggregate.segment);
        return {
          head: segment,
          raw_head: segment,
          display_head: segment,
          data,
        };
      });
    } else {
      // unsegmented
      const [row] = liftRowData;
      rows = [
        {
          display_head: DEFAULT_UNSEG_BUCKET,
          head: DEFAULT_UNSEG_BUCKET,
          raw_head: DEFAULT_UNSEG_BUCKET,
          data: buildDataFromDatedRows(row.datedRows),
        },
      ];
    }
  } else {
    rows = [];
  }
  return rows;
}

function getLiftRow(retentionType: RetentionType, timezone: string, row: SuperRow, isGroupedRow = false): LiftRow {
  const data = row.data.map(getLiftCellForIndex(row, timezone));
  const rowHead = getRowHead(row, retentionType, timezone, isGroupedRow);
  const truncatedHead =
    rowHead.length > MAX_TITLE_CELL_LENGTH ? truncateMiddle(rowHead, MAX_TITLE_CELL_LENGTH) : rowHead;
  return {
    data,
    head: rowHead,
    raw_head: rowHead,
    display_head: truncatedHead,
  };
}

function getLiftCellForIndex(row: SuperRow, timezone: string) {
  return function (value: number, valIdx: number): LiftCell {
    const completeDate = row.complete_date[valIdx];
    const incomplete = !!completeDate;
    let enteredValue = null;

    if (`segment` in row) {
      enteredValue = stringifySegment(row.segment);
    } else if (isAverageRow(row)) {
      enteredValue = `$average`; // this doesn't get consumed anywhere
    } else {
      enteredValue = formatDate(row.date, timezone);
    }

    const percent = row.rates ? row.rates[valIdx] : 0;

    return {
      date: `date` in row ? row.date : null,
      complete_date: incomplete && formatDate(completeDate, timezone, false),
      entered_value: enteredValue,
      value,
      formatted_percent: formatLiftPercent(percent),
      percent,
      incomplete,
    };
  };
}

/**
 * Turn a collection of RowWithAggregates into a flat list of rows.
 *
 * For unsegmented retention and frequency, explode the single RowWithAggregate into
 * a row for the aggregate and rows for all the birth intervals.
 *
 * For each segment of segmented retention and frequency, choose the aggregate row.
 */
function mapToUiRows({
  chartType,
  dateUnit,
  groupClauses,
  retentionBucket,
  retentionType,
  rowData,
  segments,
  timezone,
}: {
  chartType: ChartType;
  dateUnit: BirthIntervalUnit;
  groupClauses: GroupClause[];
  retentionBucket: number;
  retentionType: RetentionType;
  rowData: RowWithAggregate[];
  segments: Segment[];
  timezone: string;
}): Array<FormattedRow<RawCell>> {
  let rows: Array<FormattedRow<RawCell>>;

  const rowToCell = getCellsForRow(retentionType, timezone, groupClauses);

  if (chartType === ChartType.Table || chartType === ChartType.Line) {
    rows = rowData
      .filter((group) => !!group.aggregate)
      .map((group, idx) => {
        const averageRow = group.aggregate;
        const fmtRow = rowToCell(averageRow, idx);

        if (isSegmented(segments)) {
          const stringifiedSegment = stringifySegment(group.aggregate.segment);
          fmtRow.head = fmtRow.raw_head = stringifiedSegment;
          // Format displayed values based on property name for each segment
          fmtRow.display_head = stringifySegmentForDisplay(stringifiedSegment, groupClauses);
        }

        if (dateUnit !== NON_INTERVALIZED_VALUE) {
          fmtRow.display_cohort_size = `100%`;
          fmtRow.groupedRows = group.datedRows.map((groupedRow, groupedRowIndex) =>
            rowToCell(groupedRow, groupedRowIndex, true),
          );
        }

        if (isNumber(group.showClauseIndex)) {
          fmtRow.matching_show_clause_index = group.showClauseIndex;
        }

        return fmtRow;
      });
  } else if (chartType === ChartType.RetentionTrend || chartType === ChartType.RetentionTrendMetric) {
    if (isSegmented(segments)) {
      rows = rowData.map((segmentRow, idx) => {
        const data = segmentRow.datedRows.reduce((innerData, datedRow) => {
          if (datedRow.data.length > retentionBucket) {
            innerData.push(
              getCellForIndex(datedRow, timezone, groupClauses)(datedRow.data[retentionBucket], retentionBucket),
            );
          }
          return innerData;
        }, []);
        const segment = stringifySegment(segmentRow.aggregate.segment);
        return {
          cohort_size: segmentRow.aggregate.cohort_size,
          display_cohort_size: formatNumber(segmentRow.aggregate.cohort_size),
          data,
          display_head: segment,
          head: segment,
          raw_head: segment,
          selection_color: getChartSegmentColorCombo(idx).primary,
        };
      });
    } else {
      const [row] = rowData;
      rows = [
        {
          cohort_size: row.aggregate.cohort_size,
          display_cohort_size: formatNumber(row.aggregate.cohort_size),
          display_head: DEFAULT_UNSEG_BUCKET,
          head: DEFAULT_UNSEG_BUCKET,
          raw_head: DEFAULT_UNSEG_BUCKET,
          selection_color: getChartSegmentColorCombo(0).primary,
          data: row.datedRows.reduce((data, datedRow) => {
            if (datedRow.data.length > retentionBucket) {
              data.push(
                getCellForIndex(datedRow, timezone, groupClauses)(datedRow.data[retentionBucket], retentionBucket),
              );
            }
            return data;
          }, []),
        },
      ];
    }
  } else {
    rows = [];
  }

  return rows;
}

/**
 * Add color info to the table cell
 *
 * @param cell retention table cell
 * @param max percentage in the table
 * @param min percentage in the table
 * @return {TableCell} table cell data with color
 */
function calculateTableCellColor(cell: RawCell, max: number, min: number): TableCell {
  let combo = getRetentionColorCombo(1, false, cell.incomplete);
  // prevent an edge case where there's only one cell available
  if (max > min) {
    // math to determine the calibrated step value
    const heatPercent = (cell.percent - min) / (max - min);
    combo = getRetentionColorCombo(heatPercent, false, cell.incomplete);
  }
  return {
    ...cell,
    backgroundColor: combo.primary,
    textColor: combo.secondary,
  };
}

export function mapToRetentionCurveTableRows({
  formattedUiRows,
  bookmarkController,
  segmentKeyToPath,
  formattedLiftRowsData,
}: {
  formattedUiRows: FormattedRow[];
  bookmarkController: RetentionBookmarkController;
  segmentKeyToPath: SegmentKeyToPath;
  formattedLiftRowsData: Map<string, LiftRow>;
}): RetentionDataTableRow[] {
  const selectedSegments = new Set<string>(bookmarkController.getLegendSelectedSegments());
  const expandedSegments = new Set<string>(bookmarkController.getLegendExpandedSegments());
  const segments = bookmarkController.getSegments();

  const percentages = formattedUiRows
    .flatMap((row) => [row, ...(row.groupedRows ?? [])])
    .map((row) => row.data.filter((cell) => !cell.incomplete).map((cell) => cell.percent))
    .flat();

  const maxPercent = percentages.reduce((a, b) => Math.max(a, b), -Infinity);
  const minPercent = percentages.reduce((a, b) => Math.min(a, b), Infinity);

  const hasLift = !!formattedLiftRowsData.size;

  const formatRow = (row: FormattedRow<RawCell>): FormattedRow<TableCell> => {
    const formattedRow = {
      ...row,
      data: row.data.map(
        (cell: RawCell): TableCell => ({
          ...(hasLift ? cell : calculateTableCellColor(cell, maxPercent, minPercent)),
          clickable: true,
          //TODO: add tooltip info
          ...(cell.complete_date
            ? {
                tooltip: `${capitalize(getDataGroupName(segments, true))} will continue to qualify until the end of ${
                  cell.complete_date
                }`,
              }
            : {}),
        }),
      ),
    } as FormattedRow<TableCell>;

    if (row.groupedRows) {
      formattedRow.groupedRows = row.groupedRows.map(formatRow);
    }

    return formattedRow;
  };
  const rows: Array<FormattedRow<TableCell>> = formattedUiRows.map(formatRow);

  return rows.map((row) => {
    const selectedSegment = selectedSegments.has(row.raw_head);
    let segmentFields = {};
    if (isSegmented(segments)) {
      segmentFields = segmentKeyToPath[row.head as string].reduce((accum, _segmentValue, i) => {
        const formattedSegmentValue = row.display_head.split(` / `)[i];
        accum[`${CommonDataTableFields.SegmentPrefix}${i}`] = formattedSegmentValue;
        return accum;
      }, {});
    } else {
      segmentFields[`${CommonDataTableFields.SegmentPrefix}0`] = row.head;
    }

    let baselineRow: FormattedRow<RawCell> = null;
    if (hasLift) {
      const baselineRowKey = getLiftBaselineSegmentRawHead(
        row,
        segmentKeyToPath,
        bookmarkController.getLiftComparison(),
      );
      baselineRow = formattedUiRows.find((r) => r.raw_head === baselineRowKey);
    }

    const liftRow = formattedLiftRowsData.get(row.raw_head);
    const isPercentageView = bookmarkController.isPercentageView(row.matching_show_clause_index);

    const dataTableRow = {
      identifier: row.raw_head,
      cohortSize: row.cohort_size,
      displayCohortSize: row.display_cohort_size,
      displayHead: row.display_head,
      head: row.head,
      ...segmentFields,
      date: row.date,
      rawHead: row.raw_head,
      cells: row.data as RetentionDataTableCell[],
      isSelected: !!selectedSegment,
      ...(isNumber(row.matching_show_clause_index) ? {matchingShowClauseIndex: row.matching_show_clause_index} : {}),
      ...extractLiftFields({liftRow, baselineRow, isPercentageView}),
    } as RetentionDataTableRow;

    if (selectedSegment) {
      dataTableRow.checkedColor = row.selection_color;
    }

    if (row.groupedRows?.length) {
      dataTableRow.isCollapsed = !expandedSegments.has(row.raw_head);
      dataTableRow.groupedRows = row.groupedRows.map((groupedRow, index) => {
        let groupedLiftRow, groupedBaselineRow;
        if (liftRow && liftRow.groupedRows && baselineRow) {
          groupedLiftRow = liftRow.groupedRows[index];
          groupedBaselineRow = baselineRow.groupedRows[index];
        }
        return {
          identifier: groupedRow.raw_head,
          cohortSize: groupedRow.cohort_size,
          displayCohortSize: groupedRow.display_cohort_size,
          displayHead: groupedRow.display_head,
          head: groupedRow.head,
          date: groupedRow.date,
          rawHead: groupedRow.raw_head,
          cells: groupedRow.data as RetentionDataTableCell[],
          ...(isNumber(row.matching_show_clause_index)
            ? {matchingShowClauseIndex: row.matching_show_clause_index}
            : {}),
          ...extractLiftFields({liftRow: groupedLiftRow, baselineRow: groupedBaselineRow, isPercentageView}),
          isGroupedRow: true,
        };
      });
    }

    return dataTableRow;
  });
}

function extractLiftFields({
  liftRow,
  baselineRow,
  isPercentageView,
}: {
  liftRow: {data: LiftCell[]} | null;
  baselineRow: FormattedRow<RawCell> | null;
  isPercentageView: boolean;
}) {
  let liftComparisons = [];
  if (liftRow && baselineRow) {
    liftComparisons = liftRow.data.map((liftCell, index) => [
      // goes in the pill
      `${CommonDataTableFields.LiftComparisonDatePointPrefix}${index}`,
      isPercentageView ? liftCell.percent : liftCell.value,
    ]);
    liftComparisons = liftComparisons.concat(
      baselineRow.data.map((cell, index) => [
        // goes in the tooltip
        `${CommonDataTableFields.LiftComparisonExtraFieldPrefix}${index}`,
        (isPercentageView ? cell.percent : cell.value) || 0,
      ]),
    );
  }

  return Object.fromEntries([...liftComparisons]);
}

export function getRetentionCurveTableColumnGroups({
  formattedUiRows,
  bookmarkController,
  metricsResource,
  i18n,
  getDataCellRenderer,
}: {
  formattedUiRows: FormattedRow[];
  bookmarkController: RetentionBookmarkController;
  metricsResource: MetricsResource;
  i18n: LocalizationStore;
  getDataCellRenderer: (
    hasLift,
    bookmarkController: RetentionBookmarkController,
    metricsResource: MetricsResource,
    metricDisplayOptions: BehaviorMetricDisplay,
  ) => CellRendererFunc<RetentionDataTableRow>;
}): ColumnGroup[] {
  const hasLift = bookmarkController.getLiftComparison()?.type === LiftType.Segment;
  const segments = bookmarkController.getSegments();
  const hasMultipleRetentionMetrics = bookmarkController.hasMultipleRetentionMetrics();
  const dataCellRenderer = getDataCellRenderer(
    hasLift,
    bookmarkController,
    metricsResource,
    // Compute this once instead of doing it on the fly for every cell render
    bookmarkController.getMetricKeyToShowClauseMap(),
  );
  const numColumns =
    formattedUiRows && formattedUiRows.length ? Math.max(...formattedUiRows.map((row) => row.data.length)) : 0;

  const headerColumns = getHeaderColumns(segments, hasMultipleRetentionMetrics, i18n);
  const dataColumns = getDataColumns({numColumns, bookmarkController, dataCellRenderer, hasLift});
  return [{columns: headerColumns}, {columns: dataColumns}];
}

function getHeaderColumns(segments: Segment[], hasMultipleRetentionMetrics: boolean, i18n: LocalizationStore) {
  function getSegmentedName(groupBy: Segment[]) {
    if (isSegmented(groupBy)) {
      return getSegmentName(groupBy, i18n) || `Cohort`;
    } else {
      return `Date`;
    }
  }

  const headerColumns: TableColumn[] = [];

  let lastSegmentIndex = 0;
  if (isSegmented(segments) && hasMultipleRetentionMetrics) {
    lastSegmentIndex = segments.length;
  } else if (isSegmented(segments)) {
    lastSegmentIndex = segments.length - 1;
  }
  const cellRenderer = ({item, context}) => {
    let result;
    if (item.isGroupedRow) {
      // subRows should show their own label (e.g. date string) instead of the segment values
      result = context.field === `${CommonDataTableFields.SegmentPrefix}${lastSegmentIndex}` ? item.displayHead : ``;
    } else {
      // regular rows show the segmentation values (e.g. $mp-segment-0 -> "Chrome", $mp-segment-1 -> "USA")
      result = item[context.field];
    }
    return result;
  };

  let prefixIndex = 0;
  const commonColumnParams = {
    fixedWidth: true,
    type: TableColumnType.Segment as const,
    sticky: true,
    cellRenderer,
  };
  if (hasMultipleRetentionMetrics) {
    const column = {
      ...commonColumnParams,
      field: `${CommonDataTableFields.SegmentPrefix}0`,
      title: `Metric`,
      isSortable: true,
      contentType: PropertyType.String,
    };
    prefixIndex += 1;
    headerColumns.push(column);
  }
  if (isSegmented(segments)) {
    segments.forEach((groupBy, i) => {
      const contentType = getSegmentPropertyType(groupBy);
      const segmentField = `${CommonDataTableFields.SegmentPrefix}${i + prefixIndex}`;
      const column = {
        ...commonColumnParams,
        field: segmentField,
        title: getGroupByName(groupBy, i18n),
        isSortable: true,
        contentType,
      };
      headerColumns.push(column);
    });
  } else if (!hasMultipleRetentionMetrics) {
    const headerColumn = {
      ...commonColumnParams,
      field: `${CommonDataTableFields.SegmentPrefix}${prefixIndex}`,
      title: getSegmentedName(segments),
      isSortable: false,
      contentType: PropertyType.String,
    };
    headerColumns.push(headerColumn);
  }

  const title = `Total ${capitalize(getDataGroupName(segments, true))}`;
  const cohortSize: TableColumn = {
    field: `size`,
    title,
    sortSelectTitle: title,
    type: TableColumnType.Data,
    contentType: TableDataType.Number,
    initialWidth: 120,
    fixedWidth: true,
    sticky: true,
    isSortable: isSegmented(segments),
    sortTransform: (row) => {
      return row.cohortSize;
    },
    textColor: colors.secondaryTextColor,
    backgroundColor: colors.secondaryBackgroundColor,
    cellRenderer: ({item}) => item.displayCohortSize,
  };

  return [...headerColumns, cohortSize];
}

function getDataColumns({
  numColumns,
  bookmarkController,
  dataCellRenderer,
  hasLift,
}: {
  numColumns: number;
  bookmarkController: RetentionBookmarkController;
  dataCellRenderer: CellRendererFunc<RetentionDataTableRow>;
  hasLift: boolean;
}): TableColumn[] {
  const dataColumns = [];
  const retentionType = bookmarkController.getRetentionType();
  const retentionUnit = bookmarkController.getRetentionUnit();
  const bucketSizes = bookmarkController.getBucketSizes();
  const dateUnit = bookmarkController.getDateUnit();
  const segments = bookmarkController.getSegments();

  const dataColumnSortTransform = (row, field) => {
    const isPct = bookmarkController.isPercentageView(row.matchingShowClauseIndex);
    return row.cells[field][isPct ? `percent` : `value`];
  };
  for (let i = 0; i < numColumns; i++) {
    const title = getRetentionBucketName({
      bucketIndex: i,
      retentionType,
      retentionUnit,
      dateUnit,
      bucketSizes,
    });
    const column: TableColumn = {
      field: `${i}`,
      title,
      sortSelectTitle: title,
      type: TableColumnType.Data,
      contentType: TableDataType.Number,
      initialWidth: 90,
      fixedWidth: true,
      last: i + 1 === numColumns,
      isSortable: isSegmented(segments),
      sortTransform: dataColumnSortTransform,
      isSelectable: true,
      cellRenderer: dataCellRenderer,
      ...(hasLift
        ? {
            liftField: `${CommonDataTableFields.LiftComparisonDatePointPrefix}${i}`,
            liftExtra: `${CommonDataTableFields.LiftComparisonExtraFieldPrefix}${i}`,
            sortTransform: (row) => {
              return row[`${CommonDataTableFields.LiftComparisonDatePointPrefix}${i}`];
            },
            // set width of data table cells to be slightly wider to allow for the extra lift pill
            initialWidth: DEFAULT_LIFT_COLUMN_WIDTH,
          }
        : {}),
    };
    dataColumns.push(column);
  }
  return dataColumns;
}

function mapToCompleteDates(
  incompleteBuckets: Record<string, string[]>,
  date: string,
  tz: string,
  fallbackLength: number,
): Array<Date | null> {
  if (incompleteBuckets && incompleteBuckets[date]) {
    return incompleteBuckets[date].map((maybeDate) =>
      typeof maybeDate === `string` ? moment.tz(maybeDate, tz).toDate() : null,
    );
  } else {
    return new Array(fallbackLength).fill(null);
  }
}

export function hasRetentionData(formattedRowData: RowWithAggregate[]) {
  return formattedRowData?.some((row) =>
    row.datedRows.some((datedRow) => datedRow.cohort_size !== 0 || datedRow.data.some((v) => v !== 0)),
  );
}

function sortByAscendingDate(a: Row, b: Row) {
  return a.date.valueOf() - b.date.valueOf();
}

/**
 * Transform the API response format into Rows where
 * - dates are aligned to start of day in project time
 * - segments are renamed
 * - rows are sorted by date or size
 * - retention and addiction data looks the same
 * - aggregate data is joined to the segment it represents
 **/
export function getFormattedRowData({
  data,
  incompleteBuckets,
  segmentGroups,
  timezone,
  metricKeyLookup = null,
}: {
  data: RetentionData;
  incompleteBuckets: {
    [date: string]: Array<null | string>;
  };
  segmentGroups: Segment[];
  timezone: string;
  metricKeyLookup?: Record<string, MetricKeyInfo>;
}): RowWithAggregate[] {
  if (!data || isEmpty(data)) {
    return [];
  }

  if (isUnsegmentedData(data, !isSegmented(segmentGroups))) {
    // metricKeyLook is defined if there are multiple retention metrics
    // Thus, construct multiple row groups
    if (metricKeyLookup) {
      return constructMultiDateRowGroups(data, incompleteBuckets, timezone, metricKeyLookup);
    }
    const rows = map(omit(data, `$average`), function (row, date) {
      const dateInProjectTime = moment.tz(date, timezone).toDate();
      const numbers = getRowData(row);
      return {
        ...numbers,
        date: dateInProjectTime,
        complete_date: mapToCompleteDates(incompleteBuckets, getCalendarDate(date), timezone, numbers.data?.length),
      };
    });

    rows.sort(sortByAscendingDate);

    let averageRow: AverageRow;
    if (`$average` in data) {
      const numbers = getRowData(data[`$average`]);
      averageRow = {
        ...numbers,
        complete_date: mapToCompleteDates(incompleteBuckets, `$average`, timezone, numbers.data?.length),
        percent_data: numbers.rates || computeAveragePercentages(numbers),
      };
    }

    return [
      {
        aggregate: averageRow,
        datedRows: rows,
        showClauseIndex: 0,
      },
    ];
  }

  // segmented
  return constructMultiDateRowGroups(data, incompleteBuckets, timezone, metricKeyLookup);
}

/**
 * Collect segmented rows across birth intervals into a single RowWithAggregate per segment.
 */
function constructMultiDateRowGroups(
  data: KeyedByDate<KeyedBySegment<any>>,
  incompleteBuckets: {
    [date: string]: Array<null | string>;
  },
  projectTimezone: string,
  metricKeyLookup: Record<string, MetricKeyInfo>,
): RowWithAggregate[] {
  const segmentMap = new Map<string, RowWithAggregate>();

  for (const [bucket, , ...path] of traverseLeaves(pick(data, `$average`))) {
    const segment = path[0];
    const metricKey = segment.split(` / `)[0];
    const numbers = getRowData(bucket);

    segmentMap.set(segment, {
      aggregate: {
        ...numbers,
        segment,
        complete_date: mapToCompleteDates(incompleteBuckets, `$average`, projectTimezone, numbers.data.length),
        percent_data: numbers.rates || computeAveragePercentages(numbers),
      },
      datedRows: [],
      segment,
      showClauseIndex: metricKeyLookup ? metricKeyLookup[metricKey].showClauseIndex : 0,
    });
  }
  for (const [bucket, date, ...path] of traverseLeaves(omit(data, `$average`))) {
    const segment = path[0];
    const numbers = getRowData(bucket);

    segmentMap.get(segment).datedRows.push({
      ...numbers,
      segment,
      date: moment.tz(date, projectTimezone).toDate(),
      complete_date: mapToCompleteDates(incompleteBuckets, getCalendarDate(date), projectTimezone, numbers.data.length),
    });
  }

  const groups = [...segmentMap.values()].sort(function (a, b) {
    return b.aggregate.cohort_size - a.aggregate.cohort_size;
  });

  for (const group of groups) {
    group.datedRows.sort(sortByAscendingDate);
  }

  return groups;
}

// Local helper functions

function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Addiction>>,
): Generator<[Addiction, string, string], void, void>;
function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Retention>>,
): Generator<[Retention, string, string], void, void>;

/** iteratively yield the leaves (retention or addiction data) of the response tree with their path. */
function* traverseLeaves<TBucket>(tree: any): Generator<[TBucket, string, string], void, void> {
  function* recurse(recurseTree: any, prefix: string[]) {
    if (isLeaf(recurseTree)) {
      yield [recurseTree, ...prefix];
    } else {
      for (const key of Object.keys(recurseTree)) {
        yield* recurse(recurseTree[key], [...prefix, key]);
      }
    }
  }

  yield* recurse(tree, []);
}

function isLeaf(value: any) {
  if (Array.isArray(value)) {
    return true;
  }

  return !!(typeof value === `object` && `counts` in value && `first` in value && Array.isArray(value.counts));
}

function getRowData(bucket: Addiction | Retention): Pick<BaseRow, `cohort_size` | `data` | `rates`> {
  if (Array.isArray(bucket)) {
    return {
      cohort_size: bucket[0],
      data: bucket.slice(1),
    };
  } else {
    return {
      cohort_size: bucket.first,
      data: bucket.counts,
      rates: bucket.rates,
    };
  }
}

/** Construct an aggregate Row from all of the date interval Rows */
function computeAveragePercentages(numbers: Pick<BaseRow, `cohort_size` | `data`>): number[] {
  const percents = [];

  const {data, cohort_size: averageCohortSize} = numbers;
  if (!data) return [];

  for (let columnIndex = 0; columnIndex < data.length; columnIndex++) {
    const averageRetainedValue = data[columnIndex];
    const percent = averageCohortSize > 0 ? averageRetainedValue / averageCohortSize : 0;
    percents.push(percent);
  }
  return percents;
}

/*
 * What key should I use to get the Lift baseline segment for the row I have?
 * Returns e.g. "dashboards / United States"
 */
export function getLiftBaselineSegmentRawHead(
  row: FormattedRow,
  segmentKeyToPath: SegmentKeyToPath,
  liftComparison: LiftComparison,
) {
  const rowSegmentPath = segmentKeyToPath[row.raw_head];
  const liftBaselineSegmentPath = [...rowSegmentPath.slice(0, -1), liftComparison?.groupValue];
  return stringifySegment(liftBaselineSegmentPath);
}
